import type { PartyColumnId, PartySummary } from '@aeris/contracts'
import { formatDocument } from '@aeris/party'

import {
  dateValue,
  numberValue,
  textValue,
  type ExportColumn,
} from './export/table-export.ts'
import { PARTY_ROLE_LABELS } from './party-labels.ts'
import { formatPhone } from './phone-format.ts'

/**
 * O que cada coluna de Clientes põe no arquivo exportado — F131.
 *
 * Mesmo desenho de `product-export.ts`: todas as colunas num `Record`, para uma
 * coluna nova não sumir da planilha sem ninguém ver. O nome vira duas colunas
 * (razão social e fantasia), que na tela dividem a mesma célula.
 */

type Column = ExportColumn<PartySummary>

export const PARTY_EXPORT: Record<PartyColumnId, readonly Column[]> = {
  code: [
    {
      id: 'code',
      label: 'Cód.',
      value: (party) =>
        textValue(
          party.code === undefined || party.code === null
            ? null
            : String(party.code)
        ),
    },
    // O do sistema antigo continua na planilha: é o que o GDOOR imprimia.
    {
      id: 'externalCode',
      label: 'Cód. antigo',
      value: (party) => textValue(party.externalCode),
    },
  ],
  name: [
    { id: 'legalName', label: 'Nome', value: (party) => party.legalName },
    {
      id: 'tradeName',
      label: 'Nome fantasia',
      value: (party) => textValue(party.tradeName),
    },
  ],
  document: [
    {
      id: 'document',
      label: 'Documento',
      value: (party) =>
        party.document === null
          ? null
          : formatDocument(party.kind, party.document),
    },
  ],
  roles: [
    {
      id: 'roles',
      label: 'Papéis',
      value: (party) =>
        textValue(
          party.roles.map((role) => PARTY_ROLE_LABELS[role]).join(', ')
        ),
    },
  ],
  kind: [
    {
      id: 'kind',
      label: 'Tipo',
      value: (party) => (party.kind === 'person' ? 'Física' : 'Jurídica'),
    },
  ],
  phone: [
    {
      id: 'phone',
      label: 'Telefone / WhatsApp',
      value: (party) =>
        party.mainPhone == null ? null : formatPhone(party.mainPhone),
    },
  ],
  email: [
    {
      id: 'email',
      label: 'E-mail',
      value: (party) => textValue(party.mainEmail),
    },
  ],
  address: [
    {
      id: 'address',
      label: 'Endereço',
      value: (party) => textValue(party.mainAddress),
    },
  ],
  city: [
    {
      id: 'city',
      label: 'Cidade',
      value: (party) => textValue(party.mainCity),
    },
    { id: 'uf', label: 'UF', value: (party) => textValue(party.mainUf) },
  ],
  creditLimit: [
    {
      id: 'creditLimit',
      label: 'Limite de crédito',
      value: (party) => numberValue(party.creditLimit),
    },
  ],
  voucherBalance: [
    {
      id: 'voucherBalance',
      label: 'Saldo de vale',
      value: (party) => numberValue(party.voucherBalance),
    },
  ],
  portfolio: [
    {
      id: 'salespersonName',
      label: 'Vendedor do cliente',
      value: (party) => textValue(party.salespersonName ?? null),
    },
  ],
  salesperson: [
    {
      id: 'lastSalespersonName',
      label: 'Vendedor (última venda)',
      value: (party) => textValue(party.lastSalespersonName),
    },
  ],
  createdAt: [
    {
      id: 'createdAt',
      label: 'Cadastrado em',
      value: (party) => dateValue(party.createdAt),
    },
  ],
  lastPurchaseAt: [
    {
      id: 'lastPurchaseAt',
      label: 'Última compra',
      value: (party) => dateValue(party.lastPurchaseAt),
    },
  ],
  stateRegistration: [
    {
      id: 'stateRegistration',
      label: 'Inscrição estadual',
      value: (party) => textValue(party.stateRegistration),
    },
  ],
  status: [
    {
      id: 'status',
      label: 'Situação',
      value: (party) => (party.status === 'archived' ? 'Arquivado' : 'Ativo'),
    },
  ],
}

/** As colunas do arquivo, na ordem das que estão na tela. */
export const partyExportColumns = (
  shown: readonly { readonly id: PartyColumnId }[]
): Column[] => shown.flatMap((column) => PARTY_EXPORT[column.id])
